import type { AxiosError } from "axios";
import { useParams } from "react-router-dom";
import useSWR from "swr";
import { DelayedSpinner } from "../../../components/DelayedSpinner/DelayedSpinner";
import { endpoints } from "../../../endpoints";
import type { PIMProduct } from "../../../types/types.PIM";
import { useStoreState } from "../../../util/util";
import { PIMProductDetailsPortfolio } from "./PIMProductDetailsPortfolio";
import { useTranslation } from "react-i18next";
import { useEffect, useState } from "react";
import { ErrorPlaceholder } from "../../../components/Error";
import { CenterContainer, PageWrapper } from "../../../layout/publicPageLayout";
import { FullwidthPageWrapper } from "../../../components/ProductsLayout/SharedProductsLayout";

export const PublicProductDetailPortfolio = () => {
  const { t } = useTranslation();
  const { storefront_id } = useStoreState();
  const { productSlug } = useParams<{ productSlug: string }>();

  const [product, setProduct] = useState<PIMProduct>();

  const {
    data: productFromURL,
    error: productFromURLError,
    mutate: mutateProduct,
  } = useSWR<PIMProduct>(
    productSlug
      ? endpoints.v2_storefronts_id_pim_products_id(storefront_id, productSlug)
      : null,
    {
      onSuccess: (product) => {
        setProduct(product);
      },
      revalidateOnFocus: false,
    }
  );

  useEffect(() => {
    if (productFromURL) {
      setProduct(productFromURL);
    }
  }, [productFromURL]);

  if (productFromURLError) {
    const error = productFromURLError as AxiosError;
    if (error.response?.status === 404) {
      return (
        <PageWrapper>
          <CenterContainer>
            <ErrorPlaceholder message={t("Product not found")} />
          </CenterContainer>
        </PageWrapper>
      );
    }
    return (
      <PageWrapper>
        <CenterContainer>
          <ErrorPlaceholder
            message={t("There was an error loading this product")}
          />
        </CenterContainer>
      </PageWrapper>
    );
  }

  if (!product && !productFromURLError) {
    return (
      <PageWrapper>
        <CenterContainer>
          <DelayedSpinner />
        </CenterContainer>
      </PageWrapper>
    );
  }

  if (!product) {
    return (
      <PageWrapper>
        <CenterContainer>
          <ErrorPlaceholder message={t("Product not found")} />
        </CenterContainer>
      </PageWrapper>
    );
  }

  return (
    <FullwidthPageWrapper style={{ position: "relative" }}>
      <PIMProductDetailsPortfolio
        product={product}
        mutateProduct={mutateProduct}
        error={productFromURLError}
        updateListHeight={() => {}} // Not needed for standalone view
      />
    </FullwidthPageWrapper>
  );
};
